// Pure layout operations for the Hotwire dashboard grid. Mirror the semantics of
// the React DashboardProvider reducers (handleRemoveItem / handleDrop /
// handleCleanLayouts) and the `unusedItemsIds` memo so both surfaces manage the
// layout config identically. Every function is side-effect free and returns fresh
// objects/arrays.

import { BREAKPOINT_KEYS } from "./sqc_widget"

// Removes a widget from BOTH breakpoints — a trashed widget leaves the whole
// dashboard, not just the breakpoint it was trashed from.
export function removeItem(config, id) {
  const next = { desktop: [], mobile: [] }
  for (const breakpoint of BREAKPOINT_KEYS) {
    next[breakpoint] = (config[breakpoint] ?? []).filter(
      (item) => item.i !== id,
    )
  }
  return next
}

// Adds a widget to ONLY the given breakpoint (a drop affects the active
// breakpoint's layout alone). If the id is already present in that breakpoint it
// is replaced in place so a re-drop repositions rather than duplicates.
export function dropItem(config, breakpoint, item) {
  const next = {
    desktop: [...(config.desktop ?? [])],
    mobile: [...(config.mobile ?? [])],
  }
  const current = (config[breakpoint] ?? []).filter((it) => it.i !== item.i)
  next[breakpoint] = [...current, item]
  return next
}

// The empty base config both breakpoints reset to on "Clean".
export function cleanConfig() {
  return { desktop: [], mobile: [] }
}

// Widget ids that are registered/available but NOT placed in the given
// breakpoint — i.e. the preview cards that should be shown. Computed against the
// ACTIVE breakpoint only, matching the React `unusedItemsIds` memo.
export function unusedIds(config, breakpoint, registeredIds) {
  const used = new Set((config[breakpoint] ?? []).map((item) => item.i))
  return registeredIds.filter((id) => !used.has(id))
}

// Vertical gravity compaction — the persisted-config equivalent of GridStack's
// `float: false` (dashboard_grid_controller.js). GridStack pulls every widget up
// as far as it fits in EDIT mode, but that compaction is never captured back into
// the config we save (the rebuild runs suppressed — see
// dashboard_grid_controller#syncGridToLayout), so any gap the user watched close
// survives the save and reopens on the next CSS-grid view render. This closes the
// same gaps deterministically on save so "Edit → Done" persists what was seen.
//
// It is PER COLUMN, exactly like GridStack's gravity: each widget drops onto the
// floor formed by the already-placed widgets whose x-range it overlaps, not by a
// single whole-grid offset. A global min-y shift only matched the full-width case
// (the CONEK-5978 SqcPending banner) and left side-by-side widgets with their own
// leading gaps stranded — the ISE/ASM layouts have exactly that (CallsToDo x:0 /
// PipelineToClose x:3 on the same row), so the global version reproduced the very
// bug it set out to fix. Vertical only (never shifts x/w), and relative array
// order is preserved.
export function compactLayout(config) {
  const next = { desktop: [], mobile: [] }
  for (const breakpoint of BREAKPOINT_KEYS) {
    const items = (config[breakpoint] ?? []).map((item) => ({ ...item }))
    // Place top-to-bottom (y, then x) so each widget's floor is already settled.
    const order = items
      .map((_item, index) => index)
      .sort(
        (a, b) =>
          (items[a].y ?? 0) - (items[b].y ?? 0) ||
          (items[a].x ?? 0) - (items[b].x ?? 0),
      )
    const placed = []
    for (const index of order) {
      const item = items[index]
      const x = item.x ?? 0
      const w = item.w ?? 1
      let floor = 0
      for (const p of placed) {
        const overlapsX = x < (p.x ?? 0) + (p.w ?? 1) && (p.x ?? 0) < x + w
        if (overlapsX) floor = Math.max(floor, (p.y ?? 0) + (p.h ?? 1))
      }
      item.y = floor
      placed.push(item)
    }
    next[breakpoint] = items // original order, gravity-compacted y
  }
  return next
}
